/**
 * THE PEOPLE — as cards you can open, not a list you can only read.
 *
 * Anthony: "I have no option to edit a team member like the previous
 * setup. Should be cards that open into larger window to edit a user
 * profile in my admin settings."
 *
 * ONE CARD PER PERSON, and that is the part worth explaining. The database
 * has two tables here and they are genuinely different things:
 *
 *   profiles      a LOGIN. Somebody who can sign in, and what they may do.
 *   technicians   a PAIR OF HANDS. Somebody work is assigned to, hours are
 *                 logged against, and commission is paid to.
 *
 * Most people are both. Some are only one: a bookkeeper has a login and
 * never touches a car; a subcontractor turns wrenches and has never signed
 * in. The old screen showed them as two separate lists, so the same human
 * appeared twice and you could not tell it was one person.
 *
 * So they are joined here — on technicians.profile_id where it exists, and
 * on a normalized name where it does not — and shown as one card that says
 * which halves this person has. Fixing the join is then a visible action
 * rather than something only the database knows about.
 *
 * WHO MAY EDIT: admin and supervisor. Not a security boundary — row level
 * security already stops anybody reading another shop's people — but roles
 * and pay rates are not something a technician should be able to change on
 * their own record, which is exactly the reason to keep it off their
 * screen.
 */

import { db, api } from "../data/client.ts";
import { completionByTech, type Completion } from "../data/completion.ts";
import { esc, mount } from "../ui/dom.ts";
import { shopWords } from "../ui/words.ts";
import { cap } from "../domain/trade.ts";
import { findBarHtml, wireFindBar } from "../ui/findbar.ts";
import type { Role } from "../domain/permissions.ts";

const ROLES: Array<{ key: Role; label: string; what: string }> = [
  { key: "admin", label: "Admin", what: "Everything, including the money and the team." },
  { key: "supervisor", label: "Supervisor", what: "Everything an advisor can do, plus reports." },
  { key: "advisor", label: "Service advisor", what: "Leads, quotes, the schedule, billing." },
  { key: "technician", label: "Technician", what: "Their work. Cannot change a phone number." },
  { key: "viewer", label: "Viewer", what: "Can look. Cannot change anything." },
];

const MAY_EDIT: Role[] = ["admin", "supervisor"];

type Profile = {
  id: string; full_name: string; email: string | null;
  phone: string | null; role: Role; active: boolean;
};
type Tech = {
  id: string; name: string; phone: string | null; active: boolean; profile_id: string | null;
};

/** One human, however many rows they happen to have. */
type Person = {
  key: string;
  name: string;
  profile: Profile | null;
  tech: Tech | null;
};

const initials = (n: string) =>
  n.trim().split(/\s+/).slice(0, 2).map((w) => w[0] ?? "").join("").toUpperCase() || "?";

/** Loose enough to match "Robert wilson" with "Robert Wilson". */
const norm = (n: string) => n.trim().toLowerCase().replace(/\s+/g, " ");

function join(profiles: Profile[], techs: Tech[]): Person[] {
  const people = new Map<string, Person>();

  for (const p of profiles) {
    people.set(p.id, { key: p.id, name: p.full_name, profile: p, tech: null });
  }

  for (const t of techs) {
    // A declared link always wins over a name that merely looks the same.
    if (t.profile_id && people.has(t.profile_id)) {
      people.get(t.profile_id)!.tech = t;
      continue;
    }
    const byName = [...people.values()]
      .find((x) => x.profile && !x.tech && norm(x.profile.full_name) === norm(t.name));
    if (byName) { byName.tech = t; continue; }
    people.set(`t:${t.id}`, { key: `t:${t.id}`, name: t.name, profile: null, tech: t });
  }

  return [...people.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** Somebody who has left, or a technician taken off the board. */
const isOff = (p: Person): boolean =>
  Boolean((p.profile && !p.profile.active) || (p.tech && !p.tech.active && !p.profile));

/**
 * WHAT THIS PERSON HAS FINISHED AND WHAT IS STILL ON THEM.
 *
 * Anthony: "admin should be able to see a task completion metric under
 * team." Admins and supervisors only: it is a management number, and a
 * crew reading each other's rates is a different product decision from the
 * one that was asked for.
 *
 * THE COUNT COMES BEFORE THE RATE, on purpose. Somebody handed two jobs
 * and finishing both is at 100%; somebody handed nine and finishing seven
 * is at 78% and did three times the work. Leading with the percentage
 * quietly tells a crew to take fewer jobs. Under four jobs there is no
 * percentage at all, because it would be noise dressed as a measurement.
 */
let howTheyAreDoing: Map<string, Completion> = new Map();

function scoreOf(p: Person): string {
  const c = p.tech ? howTheyAreDoing.get(p.tech.id) : undefined;
  if (!c || (!c.finished && !c.open)) return "";
  return `<span class="pcard-score">
    <b>${c.finished}</b> finished
    ${c.open ? `<i>&middot; ${c.open} still on them</i>` : ""}
    ${c.late ? `<i class="bad">&middot; ${c.late} past the day</i>` : ""}
    ${c.rate != null ? `<em>${Math.round(c.rate * 100)}%</em>` : ""}
  </span>`;
}

/**
 * The badge that would have caught Ron. Only ever shown on somebody who
 * HAS a login, because "never signed in" about a person with no login is
 * noise -- the card already says "no login" for them.
 */
function loginBadge(p: Person): string {
  if (!p.profile) return "";
  const st = loginState.get(p.profile.id);
  if (!st) return "";
  if (!st.signed_in) return `<span class="pill s-cancelled">never signed in</span>`;
  if (!st.confirmed) return `<span class="pill warn">email not confirmed</span>`;
  return "";
}

function card(p: Person): string {
  const role = p.profile?.role;
  const off = (p.profile && !p.profile.active) || (p.tech && !p.tech.active && !p.profile);
  return `
    <button class="pcard${off ? " off" : ""}" data-k="${esc(p.key)}">
      <span class="pcard-av">${esc(initials(p.name))}</span>
      <span class="pcard-name">${esc(p.name)}</span>
      <span class="pcard-tags">
        ${role ? `<span class="pill r-${esc(role)}">${esc(role)}</span>` : ""}
        ${p.tech ? `<span class="pill s-scheduled">turns wrenches</span>` : ""}
        ${!p.profile ? `<span class="pill">no login</span>` : ""}
        ${loginBadge(p)}
        ${off ? `<span class="pill s-cancelled">inactive</span>` : ""}
      </span>
      <span class="pcard-sub">${esc(p.profile?.email ?? p.tech?.phone ?? "nothing on file")}</span>
      ${scoreOf(p)}
    </button>`;
}

/**
 * WHOSE LOGIN HAS NEVER BEEN USED.
 *
 * Ron at Manko was approved on 2 September, could not get in, and nobody
 * knew until he rang on the 17th. Every screen in this product said he was
 * fine, because "admin", "active" and "paid" all live in profiles and the
 * truth lives in the auth service. The same query that afternoon found six
 * active people across five shops who had never signed in, four of them
 * the shop's own admin.
 *
 * So the card says it now, before somebody rings. Empty when the call
 * fails, because a team page that will not draw because one extra fact is
 * missing is worse than a team page without the fact.
 */
type LoginState = { signed_in: string | null; confirmed: boolean };
let loginState = new Map<string, LoginState>();

async function loadLoginState(): Promise<Map<string, LoginState>> {
  const map = new Map<string, LoginState>();
  try {
    const sb = await db();
    const { data: { session } } = await sb.auth.getSession();
    if (!session) return map;
    const res = await fetch(api("/api/team/status"), {
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (!res.ok) return map;
    const body = await res.json() as {
      people?: Array<{ id: string; signed_in: string | null; confirmed: boolean }>;
    };
    for (const r of body.people ?? []) map.set(r.id, { signed_in: r.signed_in, confirmed: r.confirmed });
  } catch { /* the list matters more than the badge */ }
  return map;
}

let people: Person[] = [];

/** Who may see how everybody is doing. A management number, not a crew one. */
const BOSS_SEES: Role[] = ["admin", "supervisor"];

export async function teamPage(role: Role = "viewer"): Promise<void> {
  const mayEdit = MAY_EDIT.includes(role);

  mount(`<div class="page">
      <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
        <h1>Team</h1>
        <p>Everybody at the shop. A person can have a login, a pair of hands, or both &mdash;
           the card says which.${mayEdit ? " Open one to change it." : ""}</p>
        ${mayEdit ? `<button class="btn primary" id="add-person">Add somebody</button>` : ""}
      </header>
      <div id="findhost">${findBarHtml("Find somebody")}</div>
      <div class="pcards" id="team"><div class="loading">Reading&hellip;</div></div>
    </div>`);

  document.getElementById("add-person")?.addEventListener("click", () => addSheet(role));

  const host = document.getElementById("team");
  if (!host) return;

  try {
    const sb = await db();
    const [pr, tc, score, logins] = await Promise.all([
      sb.from("profiles").select("id,full_name,email,phone,role,active").order("full_name"),
      sb.from("technicians").select("id,name,phone,active,profile_id").order("name"),
      // Only the people who can act on it get asked for it. A failure here
      // must not take the team page down with it — the page's job is the
      // list, and the score is an addition to it.
      BOSS_SEES.includes(role)
        ? completionByTech().catch(() => new Map<string, Completion>())
        : Promise.resolve(new Map<string, Completion>()),
      /* Same rule as the score above: only the people who can fix a dead
         login are asked to look at one. */
      BOSS_SEES.includes(role) ? loadLoginState() : Promise.resolve(new Map<string, LoginState>()),
    ]);
    if (pr.error) throw new Error(pr.error.message);
    howTheyAreDoing = score;
    loginState = logins;

    people = join((pr.data ?? []) as unknown as Profile[], (tc.data ?? []) as unknown as Tech[]);

    /**
     * ACTIVE PEOPLE FIRST, and the rest behind a button.
     *
     * The shop has 34 technicians on file and a working crew of about a
     * dozen; most of the rest have left. Sorted alphabetically together,
     * you scroll past somebody who moved away in March to reach the person
     * standing in the bay. They are kept, because their hours and their
     * jobs are part of the record, and folded away because nobody is
     * looking for them.
     */
    const here = people.filter((p) => !isOff(p));
    const gone = people.filter(isOff);
    let showingGone = false;

    const paint = (): void => {
      host.innerHTML = people.length
        ? `${here.map(card).join("")}
           ${gone.length ? (showingGone
             ? `<div class="goneline">Left the shop &middot; ${gone.length}</div>
                ${gone.map(card).join("")}`
             : `<button class="pcard showgone" id="show-gone">
                  <span class="pcard-av">+${gone.length}</span>
                  <span class="pcard-name">Everybody who has left</span>
                  <span class="pcard-sub">${esc(gone.slice(0, 3).map((x) => x.name).join(", "))}${
                    gone.length > 3 ? ` and ${gone.length - 3} more` : ""}</span>
                </button>`) : ""}`
        : `<div class="empty">Nobody yet.</div>`;

      if (mayEdit) {
        host.querySelectorAll<HTMLButtonElement>(".pcard:not(.showgone)").forEach((b) =>
          b.addEventListener("click", () => {
            const p = people.find((x) => x.key === b.dataset["k"]);
            if (p) void openPerson(p, role);
          }));
      }
      document.getElementById("show-gone")?.addEventListener("click", () => {
        showingGone = true; paint();
      });
      wireFindBar({ hostId: "findhost", listId: "team", noun: "person" });
    };
    paint();
  } catch (e) {
    host.innerHTML = `<div class="empty bad">${esc(e instanceof Error ? e.message : String(e))}</div>`;
  }
}

/* ── the editor ───────────────────────────────────────────────────── */

function shut(): void { document.getElementById("psheet")?.remove(); }

async function openPerson(p: Person, viewerRole: Role): Promise<void> {
  shut();

  const pr = p.profile;
  const tc = p.tech;

  /* Their share, read before the sheet draws so the boxes are never empty
     for a moment and then filled — which reads as "no commission set" for
     exactly long enough for somebody to type over it. */
  type Share = { commission_pct: number | null; parts_pct: number | null };
  let terms: Share | null = null;
  let accountId = "";
  if (tc) {
    const sbT = await db();
    const [t, row] = await Promise.all([
      sbT.from("pay_terms").select("commission_pct,parts_pct")
        .eq("technician_id", tc.id).maybeSingle(),
      sbT.from("technicians").select("account_id").eq("id", tc.id).maybeSingle(),
    ]);
    terms = (t.data ?? null) as Share | null;
    accountId = ((row.data ?? null) as { account_id?: string } | null)?.account_id ?? "";
  }

  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back" id="psheet">
      <div class="sheet wide" role="dialog" aria-modal="true">
        <div class="sheet-head">
          <span class="avatar big">${esc(initials(p.name))}</span>
          <div><h2>${esc(p.name)}</h2>
            <div class="quiet">${pr ? "Has a login" : "No login"}${
              tc ? " &middot; turns wrenches" : ""}</div>
            ${pr ? `<a class="navlink" href="#/me/${esc(pr.id)}" id="p-page">Their page</a>` : ""}
          </div>
          <button class="sheet-x" id="px" aria-label="Close">&times;</button>
        </div>

        <div class="sheet-body">
          ${pr ? `
            <div class="ed-sec">Their login</div>
            <label class="field"><span>Name</span>
              <input id="e-name" value="${esc(pr.full_name)}"></label>
            <label class="field"><span>Email</span>
              <input id="e-email" type="email" value="${esc(pr.email ?? "")}"></label>
            <label class="field"><span>Phone</span>
              <input id="e-phone" value="${esc(pr.phone ?? "")}"></label>

            <div class="ed-sec">What they can do</div>
            <div class="rolepick" id="e-role">
              ${ROLES.map((r) => `
                <button class="rolebtn ${r.key === pr.role ? "on" : ""}" data-r="${r.key}">
                  <b>${esc(r.label)}</b><i>${esc(r.what)}</i></button>`).join("")}
            </div>

            <label class="check"><input type="checkbox" id="e-active" ${pr.active ? "checked" : ""}>
              <span>They can sign in</span></label>
            <p class="quiet small">Turning this off is how somebody leaves. It keeps their work
               and their history; it only stops the door opening.</p>
          ` : `
            <div class="ed-sec">No login</div>
            <p class="quiet">This person is on the board as a pair of hands but cannot sign in.</p>`}

          <div class="ed-sec">Signing in</div>
          <p class="quiet small">A link that lets them set their own password. It comes back to
             this screen rather than going by email, because email from here is unreliable and a
             link you can text arrives.</p>
          <label class="field"><span>Their email</span>
            <input id="e-loginmail" type="email"
                   value="${esc(pr?.email ?? "")}"></label>
          <label class="field"><span>Set their password</span>
            <input id="e-pw" type="text" autocomplete="off" spellcheck="false"></label>
          <div class="pwrow">
            <button class="btn primary" id="set-pw">Set the password</button>
            <button class="btn ghost" id="mk-link">Or send a link instead</button>
          </div>
          <p class="quiet small">Setting it here is the reliable way. They sign in with it
             straight away and can change it later. A link depends on email arriving.</p>
          <div id="link-out"></div>

          ${tc ? `
            <div class="ed-sec">On the board</div>
            <label class="field"><span>Name as it appears on jobs</span>
              <input id="t-name" value="${esc(tc.name)}"></label>
            <label class="field"><span>Phone</span>
              <input id="t-phone" value="${esc(tc.phone ?? "")}"></label>
            <label class="check"><input type="checkbox" id="t-active" ${tc.active ? "checked" : ""}>
              <span>Available for work</span></label>

            <!-- WHAT THEY ARE PAID, IN THE PRODUCT.
                 pay_terms has existed since migration 0006 and the payroll
                 screen reads it on every run. Nothing in this system ever
                 WROTE to it. So "Tico makes 80% labour and 50% parts" and
                 "Jonathan makes 80% and 50%" were both requests that could
                 only be answered by somebody hand-writing SQL against the
                 live database, and a shop cannot run that way.
                 Percentages are typed as percentages and stored as the
                 fractions the commission code already expects, because
                 nobody at a desk thinks in 0.8. -->
            <div class="ed-sec">What they are paid</div>
            <div class="ed-two">
              <label class="field"><span>Labour %</span>
                <input id="t-lab" inputmode="decimal"
                  value="${terms?.commission_pct != null
                    ? esc(String(Math.round(terms.commission_pct * 1000) / 10)) : ""}"></label>
              <label class="field"><span>${esc(cap(shopWords().partsLabel))} %</span>
                <input id="t-part" inputmode="decimal"
                  value="${terms?.parts_pct != null
                    ? esc(String(Math.round(terms.parts_pct * 1000) / 10)) : ""}"></label>
            </div>
            <p class="quiet small">Their share of the labour and of the parts margin on
               every job they are the lead on. Leave both empty if they are not on
               commission.</p>
            <p class="quiet small">Turning this off takes them out of the assignment list.
               Jobs they already have stay theirs.</p>
            ${pr && !tc.profile_id ? `
              <p class="quiet small">These two records are matched by name only.
                <button class="linkbtn" id="t-link">Link them properly</button> so hours and
                commission follow the right person.</p>` : ""}
          ` : ""}
        </div>

        <div class="sheet-acts">
          <button class="btn primary" id="p-save">Save</button>
          <button class="btn ghost" id="p-cancel">Cancel</button>
        </div>
        <div class="msg-slot" id="p-msg"></div>
      </div>
    </div>`);

  let picked: Role = pr?.role ?? "technician";
  document.querySelectorAll<HTMLButtonElement>("#e-role .rolebtn").forEach((b) =>
    b.addEventListener("click", () => {
      // An admin demoting themselves locks the shop out of its own team
      // screen, and there is no way back from inside the app.
      if (pr && pr.id === myId && b.dataset["r"] !== "admin" && viewerRole === "admin") {
        say("You cannot take your own admin away — somebody else would have to give it back.");
        return;
      }
      picked = b.dataset["r"] as Role;
      document.querySelectorAll("#e-role .rolebtn").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
    }));

  const say = (m: string, bad = true) => {
    const el = document.getElementById("p-msg");
    if (el) el.innerHTML = `<p class="msg ${bad ? "bad" : "ok"}">${esc(m)}</p>`;
  };
  const val = (id: string) =>
    (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";
  const on = (id: string) =>
    (document.getElementById(id) as HTMLInputElement | null)?.checked ?? false;

  document.getElementById("px")?.addEventListener("click", shut);
  document.getElementById("p-cancel")?.addEventListener("click", shut);
  // Following a link out of a modal has to take the modal with it, or
  // the next page loads underneath a sheet that will not go away.
  document.getElementById("p-page")?.addEventListener("click", shut);
  document.getElementById("psheet")?.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === "psheet") shut();
  });

  document.getElementById("set-pw")?.addEventListener("click", async () => {
    const b = document.getElementById("set-pw") as HTMLButtonElement;
    const out = document.getElementById("link-out");
    const target = p.profile?.id;
    if (!target) { say("This person has no record to attach a login to yet."); return; }

    const pw = val("e-pw");
    if (pw.length < 8) { say("Use at least eight characters."); return; }

    b.disabled = true; b.textContent = "Setting…";
    try {
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(api("/api/team/set-password"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ profile_id: target, password: pw, email: val("e-loginmail") }),
      });
      const body = await res.json() as { ok?: boolean; email?: string; error?: string };
      b.disabled = false; b.textContent = "Set the password";
      if (!res.ok || !body.ok) { say(body.error ?? "That did not work."); return; }
      if (out) {
        out.innerHTML = `
          <div class="linkout">
            <div class="linkout-h">Ready</div>
            <p class="quiet">${esc(body.email ?? "")} can sign in with that password now.
               Tell them, and they can change it whenever they like.</p>
          </div>`;
      }
      // Off the screen the moment it has been used. A password sitting in
      // a box on a shared tablet is a password on a shared tablet.
      (document.getElementById("e-pw") as HTMLInputElement).value = "";
    } catch (e) {
      b.disabled = false; b.textContent = "Set the password";
      say(e instanceof Error ? e.message : String(e));
    }
  });

  document.getElementById("mk-link")?.addEventListener("click", async () => {
    const b = document.getElementById("mk-link") as HTMLButtonElement;
    const out = document.getElementById("link-out");
    const target = p.profile?.id;
    if (!target) {
      say("This person has no record to attach a login to yet.");
      return;
    }
    b.disabled = true; b.textContent = "Making it…";
    try {
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(api("/api/team/login-link"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ profile_id: target, email: val("e-loginmail") }),
      });
      const body = await res.json() as { link?: string; note?: string; error?: string; created?: boolean };
      b.disabled = false; b.textContent = "Or send a link instead";
      if (!res.ok || !body.link) { say(body.error ?? "That did not work."); return; }
      if (out) {
        out.innerHTML = `
          <div class="linkout">
            <div class="linkout-h">${body.created ? "Login created" : "Link ready"}</div>
            <textarea class="linkout-v" readonly rows="3">${esc(body.link)}</textarea>
            <div class="linkout-act">
              <button class="btn sm" id="link-copy">Copy the link</button>
              <a class="btn sm ghost" href="sms:?&body=${encodeURIComponent(
                `Set your Shop Desk password: ${body.link}`)}">Text it</a>
            </div>
            <div class="fine">${esc(body.note ?? "")}</div>
          </div>`;
        document.getElementById("link-copy")?.addEventListener("click", async () => {
          const btn = document.getElementById("link-copy") as HTMLButtonElement;
          try { await navigator.clipboard.writeText(body.link!); btn.textContent = "Copied"; }
          catch {
            // Clipboard is blocked in plenty of places. Select it instead
            // so a long press still works.
            (out.querySelector(".linkout-v") as HTMLTextAreaElement).select();
            btn.textContent = "Selected — copy it";
          }
        });
      }
    } catch (e) {
      b.disabled = false; b.textContent = "Or send a link instead";
      say(e instanceof Error ? e.message : String(e));
    }
  });

  document.getElementById("t-link")?.addEventListener("click", async () => {
    if (!pr || !tc) return;
    try {
      const sb = await db();
      const { error } = await sb.from("technicians").update({ profile_id: pr.id }).eq("id", tc.id);
      if (error) throw new Error(error.message);
      shut(); void teamPage(viewerRole);
    } catch (e) { say(e instanceof Error ? e.message : String(e)); }
  });

  document.getElementById("p-save")?.addEventListener("click", async () => {
    const btn = document.getElementById("p-save") as HTMLButtonElement;
    btn.disabled = true; btn.textContent = "Saving…";
    try {
      const sb = await db();

      if (pr) {
        const name = val("e-name");
        if (!name) throw new Error("A person needs a name.");
        const { error } = await sb.from("profiles").update({
          full_name: name,
          email: val("e-email") || null,
          phone: val("e-phone") || null,
          role: picked,
          active: on("e-active"),
        }).eq("id", pr.id);
        if (error) throw new Error(error.message);
      }

      if (tc) {
        const tname = val("t-name");
        if (!tname) throw new Error(`A ${shopWords().techLabel} needs a name on the board.`);
        const { error } = await sb.from("technicians").update({
          name: tname, phone: val("t-phone") || null, active: on("t-active"),
        }).eq("id", tc.id);
        if (error) throw new Error(error.message);

        /* Typed as 80, stored as 0.8. Blank means "not on commission" and
           has to write null rather than nothing, so clearing a share
           actually clears it. */
        const pct = (id: string): number | null => {
          const raw = val(id).replace(/[^0-9.]/g, "");
          if (!raw) return null;
          const n = Number(raw);
          if (!Number.isFinite(n) || n < 0 || n > 100) {
            throw new Error("A share is a number between 0 and 100.");
          }
          return Math.round(n * 10) / 1000;
        };
        const lab = pct("t-lab");
        const part = pct("t-part");
        const { error: te } = await sb.from("pay_terms").upsert({
          technician_id: tc.id, account_id: accountId,
          basis: lab == null && part == null ? null : "commission",
          commission_pct: lab, parts_pct: part,
          updated_at: new Date().toISOString(),
        }, { onConflict: "technician_id" });
        if (te) throw new Error(te.message);
      }

      shut();
      void teamPage(viewerRole);
    } catch (e) {
      btn.disabled = false; btn.textContent = "Save";
      say(e instanceof Error ? e.message : String(e));
    }
  });
}

/** Set at boot so the screen can stop somebody demoting themselves. */
let myId = "";
export const rememberMe = (id: string): void => { myId = id; };

/**
 * ADDING SOMEBODY, WHICH THERE WAS NO WAY TO DO.
 *
 * Anthony: "I also don't have an option to add a team member." He did not,
 * anywhere — every person on this system arrived through the migration or
 * through somebody running SQL. That is why five of twelve staff had never
 * signed in: nobody could finish setting them up from inside the product.
 *
 * FOUR FIELDS AND A ROLE. A name, an email because that is how they sign
 * in, a mobile because that is how the shop reaches them, and where they
 * work out of — the shop runs more than one market and the person in Los
 * Angeles is not who you send to Hemet at four in the afternoon.
 *
 * NO PASSWORD HERE. The account is made confirmed and empty, and the two
 * buttons that already exist on their card do the rest: set one, or send
 * them a link. One way in rather than two, and the careful one.
 */
function addSheet(role: Role): void {
  document.getElementById("addsheet")?.remove();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back" id="addsheet">
      <div class="sheet" role="dialog" aria-modal="true">
        <div class="sheet-head"><div><h2>Add somebody</h2>
          <div class="quiet">They get a login and a place on the team. You set their
            password, or send them a link, from their card afterwards.</div></div>
          <button class="sheet-x" id="ap-x" aria-label="Close">&times;</button></div>
        <div class="sheet-body">
          <label class="field"><span>Their name</span>
            <input id="ap-name" autocomplete="name"></label>
          <label class="field"><span>Email &mdash; this is how they sign in</span>
            <input id="ap-email" type="email" autocomplete="off"></label>
          <label class="field"><span>Mobile</span>
            <input id="ap-phone" type="tel" inputmode="tel"></label>
          <label class="field"><span>Works out of</span>
            <input id="ap-market"></label>
          <div class="field"><span>What they do</span>
            <div class="roles" id="ap-roles">
              ${ROLES.map((r, i) => `
                <button class="rolebtn ${i === 0 ? "" : ""}${
                  r.key === "technician" ? " on" : ""}" data-r="${r.key}">
                  <b>${esc(r.label)}</b><i>${esc(r.what)}</i></button>`).join("")}
            </div>
          </div>
        </div>
        <div class="sheet-acts">
          <button class="btn primary" id="ap-go">Add them</button>
          <button class="btn ghost" id="ap-cancel">Cancel</button>
        </div>
        <div class="msg-slot" id="ap-msg"></div>
      </div>
    </div>`);

  const shut = () => document.getElementById("addsheet")?.remove();
  document.getElementById("ap-x")?.addEventListener("click", shut);
  document.getElementById("ap-cancel")?.addEventListener("click", shut);

  let chosen = "technician";
  document.querySelectorAll<HTMLButtonElement>("#ap-roles .rolebtn").forEach((b) =>
    b.addEventListener("click", () => {
      chosen = b.dataset["r"] ?? "technician";
      document.querySelectorAll("#ap-roles .rolebtn").forEach((x) => x.classList.remove("on"));
      b.classList.add("on");
    }));

  const val = (id: string) =>
    (document.getElementById(id) as HTMLInputElement | null)?.value.trim() ?? "";

  document.getElementById("ap-go")?.addEventListener("click", async () => {
    const btn = document.getElementById("ap-go") as HTMLButtonElement;
    const msg = document.getElementById("ap-msg");
    const say = (t: string, good = false) => {
      if (msg) msg.innerHTML = `<p class="msg ${good ? "ok" : "bad"}">${esc(t)}</p>`;
    };
    if (val("ap-name").length < 2) return say("They need a name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val("ap-email"))) {
      return say("That email does not look right.");
    }
    btn.disabled = true; btn.textContent = "Adding\u2026";
    try {
      const sb = await db();
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(api("/api/team/add"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({
          full_name: val("ap-name"), email: val("ap-email"),
          phone: val("ap-phone"), market: val("ap-market"), role: chosen,
        }),
      });
      const out = await res.json() as { ok?: boolean; error?: string };
      if (!res.ok || !out.ok) {
        btn.disabled = false; btn.textContent = "Add them";
        return say(out.error ?? "That did not work.");
      }
      say(`${val("ap-name")} is on the team. Open their card to set a password `
        + `or send them a link.`, true);
      setTimeout(() => { shut(); void teamPage(role); }, 1400);
    } catch (e) {
      btn.disabled = false; btn.textContent = "Add them";
      say(e instanceof Error ? e.message : String(e));
    }
  });

  (document.getElementById("ap-name") as HTMLInputElement | null)?.focus();
}
